import type { ReactNode } from 'react';
import { cn } from '@ai-tutor/ui';

interface FocusRingProps {
  /** 0–1 of the phase done, or `null` for Flow (counts up, no end). */
  progress: number | null;
  tone: 'focus' | 'paused' | 'break';
  size?: 'sm' | 'md' | 'lg';
  /** Alignment override - the ring centres itself by default. */
  className?: string;
  children: ReactNode;
}

const RADIUS = 52;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const TONE = {
  focus: 'text-brand-500 dark:text-brand-300',
  paused: 'text-muted-foreground',
  break: 'text-brand-600 dark:text-brand-200',
} as const;

const SIZE = {
  sm: 'h-24 w-24',
  md: 'h-36 w-36',
  lg: 'h-48 w-48 sm:h-52 sm:w-52',
} as const;

/**
 * The timer face: a ring that fills as the phase runs, with the time inside.
 * Decorative - the time in `children` is what is read out.
 */
export function FocusRing({
  progress,
  tone,
  size = 'lg',
  className,
  children,
}: FocusRingProps) {
  const done = progress === null ? 1 : Math.min(1, Math.max(0, progress));
  return (
    <div className={cn('relative mx-auto', SIZE[size], className)}>
      <svg
        viewBox='0 0 120 120'
        className={cn('h-full w-full -rotate-90', TONE[tone])}
        aria-hidden='true'
      >
        <circle
          cx='60'
          cy='60'
          r={RADIUS}
          fill='none'
          strokeWidth='4'
          className='stroke-brand-200/70 dark:stroke-brand-500/20'
        />
        <circle
          cx='60'
          cy='60'
          r={RADIUS}
          fill='none'
          stroke='currentColor'
          strokeWidth='4'
          strokeLinecap='round'
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={CIRCUMFERENCE * (1 - done)}
          // Flow has no end to fill toward: a faint full ring instead.
          className={cn(
            'transition-[stroke-dashoffset] duration-slow',
            progress === null && 'opacity-30'
          )}
        />
      </svg>
      <div className='absolute inset-0 flex flex-col items-center justify-center text-center'>
        {children}
      </div>
    </div>
  );
}
